import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import type { OnboardingHintId } from '../../constants';
import type { StoreType } from '../../reducers';
import { selectTriggeredHints } from '../../reducers/user.selectors';
import { useTriggeredHint } from '../../utils/useTriggeredHint';
import { Hint, type HintProps } from '../Hint';

export interface OnboardingHintProps extends Omit<HintProps, 'open' | 'onClose'> {
	id: OnboardingHintId;
	onShow?: () => void;
	onClose?: () => void;
	preventLocalStorage?: boolean;
	prerequisites?: Array<string>;
	externalDismiss?: (fn: () => void) => void;
	dontShow?: boolean;
}

export const OnboardingHint: React.FC<OnboardingHintProps> = ({ id, ...props }) => {
	// Hint "seen" state now lives on the user object (server-side), keyed by the
	// same `hint-<id>` string that was previously used in localStorage.
	const { hasSeen, record } = useTriggeredHint(`hint-${id}`);
	const triggeredHints = useSelector(selectTriggeredHints);
	// triggeredHints is empty until the user loads; gate to avoid a false "unseen".
	const isUserLoaded = useSelector((state: StoreType) => Boolean(state.user.me));

	// Local, session-only dismissal so the hint closes immediately on dismiss —
	// including the `preventLocalStorage` case, which never persists server-side.
	const [dismissed, setDismissed] = useState(false);

	const prerequisitesMet = props.prerequisites?.length
		? props.prerequisites.every((prerequisite) => triggeredHints.includes(`hint-${prerequisite}`))
		: true;

	const shouldShow = isUserLoaded && prerequisitesMet && !hasSeen && !dismissed;

	const hasFiredOnShow = useRef(false);
	// biome-ignore lint/correctness/useExhaustiveDependencies: pre-Biome hook deps kept as-is; changing the deps list alters effect timing
	useEffect(() => {
		// Fire onShow once, when the hint first becomes eligible.
		if (!hasFiredOnShow.current && shouldShow) {
			hasFiredOnShow.current = true;
			props.onShow?.();
		}
	}, [shouldShow]);

	const dismiss = () => {
		if (!props.preventLocalStorage) {
			record();
		}
		setDismissed(true);
		props?.onClose?.();
	};

	props.externalDismiss?.(dismiss);
	const actions = props.actions;

	const createOnClickHandler = (
		originalOnClick: () => void,
		options?: { withoutDismiss?: boolean }
	) => {
		if (options?.withoutDismiss) {
			return originalOnClick;
		}
		return () => {
			originalOnClick();
			dismiss();
		};
	};

	if (actions) {
		if (actions.primary) {
			actions.primary.onClick = createOnClickHandler(actions.primary.onClick);
		}
		if (actions.secondary) {
			actions.secondary.onClick = createOnClickHandler(actions.secondary.onClick, {
				withoutDismiss: actions.secondary.withoutDismiss,
			});
		}
	}

	return (
		<Hint {...props} actions={actions} open={props.dontShow ? false : shouldShow} onClose={dismiss} />
	);
};

export default OnboardingHint;
